import { Component, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { ClassNames, historyReplaceState } from '@/utils';
import { message } from 'antd';
import LazyLoad from 'react-lazyload';
import dynamic from 'next/dynamic';
import CookieConsent from '@/components/Layout/CookieBot/CookieConsent';
import { GlobalTracker } from '@/utils/Tracker';
import ScriptSnippets from '@/components/Layout/ScriptSnippets';
import styles from './index.module.less';

const Header = dynamic(import('@/components/Header'));
const CookieBot = dynamic(import('./CookieBot'), { ssr: false });
const Footer = dynamic(import('@/components/Footer'), { ssr: false });
const TaxModalForMX = dynamic(import('@/components/TaxModalForMX'), { ssr: false });
const AreaSelectorModal = dynamic(import('@/components/AreaSelectorModal'), { ssr: false });
const AreaSelectorConfirmModal = dynamic(import('@/components/AreaSelectorConfirmModal'), { ssr: false });
const ArrivalNoticeModal = dynamic(import('@/pages/product/components/ArrivalNoticeModal'), { ssr: false });
const OrderSubmitLoading = dynamic(import('@/pages/order/components/OrderSubmitLoading'), { ssr: false });
const PopupPlayer = dynamic(import('@/components/PopupPlayer'), { ssr: false });
const LiveFB = dynamic(import('@/components/LiveFB'), { ssr: false });
const PageToolBar = dynamic(import('@/components/PageToolBar'));
const Loading = dynamic(import('@/components/SpinLoading'));
const SubscribeModal = dynamic(import('@/components/SubscribeModal/index'), { ssr: false });
const PageServerError = dynamic(import('../ErrorPage/PageServerError'), { ssr: false });
const ComponentsErrorBoundary = dynamic(import('../ErrorBoundary/ComponentsErrorBoundary'));
const SearchBar = dynamic(import('@/components/GlobalSearchBar'), { ssr: false });
const LiveShoppingModal = dynamic(import('@/components/LiveShoppingModal'), { ssr: false });

function analysis() {
  // Insta360
  window._ist?.('create', { appId: 3, lang: 'en', singlePage: true });
}

@inject('area', 'auth', 'order', 'product', 'app')
@observer
class Layout extends Component {
  componentDidMount() {
    const { initAreaNavigation } = this.props.area;
    const { isMobile } = this.props.app;
    this.props.area.initListCountry();
    initAreaNavigation();
    analysis();
    window.addEventListener('popstate', historyReplaceState);

    message.config({
      top: isMobile ? '160px' : '320px',
      duration: 2,
      maxCount: 5,
    });

    this.initUserActiveCustomEvent();

    // 初始化购物车数据到cookie
    this.props.order.initCartItemsToCookie();
  }

  initUserActiveCustomEvent = () => {
    const { isOrderResultPage, isMobile, isProductPage } = this.props.app;
    this.userActiveEvent = new CustomEvent('InstaUserActive');

    if (isOrderResultPage) {
      this.userActiveListener({ type: 'order result auto action' });
      return;
    }

    // 如果没有交互，产品页在1s后也会自动触发更新，避免部分展示较短的产品页详情模块无法出现
    if (isProductPage) {
      setTimeout(() => {
        this.userActiveListener({ type: 'product page auto action' });
      }, 1000);
    }

    if (isMobile) {
      window.addEventListener('touchstart', this.userActiveListener);
    } else {
      window.addEventListener('mousemove', this.userActiveListener);
    }
    window.addEventListener('click', this.userActiveListener);
    window.addEventListener('scroll', this.userActiveListener);
  };

  /**
   *
   * @description 用户与页面发生交互
   */
  userActiveListener = (e) => {
    if (this.InstaUserActive) return;
    const { setGlobalValue } = this.props.app;
    window.InstaUserActive = true;
    setGlobalValue('InstaUserActive', true);
    window.dispatchEvent(this.userActiveEvent);
    GlobalTracker.Common({ category: 'UserActivePageType', action: e.type });
    window.removeEventListener('touchstart', this.userActiveListener);
    window.removeEventListener('mousemove', this.userActiveListener);
    window.removeEventListener('click', this.userActiveListener);
    window.removeEventListener('scroll', this.userActiveListener);

    // 交互后加载第三方脚本，不依赖CookieConsent
    ScriptSnippets.Forter({ app: this.props.app });
  };

  componentWillUnmount() {
    window.removeEventListener('popstate', historyReplaceState);
  }

  render() {
    const { app, area } = this.props;
    const ClassName = [
      styles.page,
      app.G_PageMenusFixed ? '' : styles.static,
      app.G_IsProductPage ? styles.isProduct : '',
    ];

    return (
      <div id='page' className={ClassNames(ClassName)}>
        <ComponentsErrorBoundary name='G_AreaSelectorConfirmModal'>
          {area.areaNavigationVisible && <AreaSelectorConfirmModal />}
        </ComponentsErrorBoundary>

        {!app.G_HideHeaderFooter && (
          <ComponentsErrorBoundary name='G_Header'>
            <Header />
          </ComponentsErrorBoundary>
        )}
        <div id='g-main' className={styles.main}>
          <ComponentsErrorBoundary name='G_Layout' errorCatchDisplay={<PageServerError hideBtn />}>
            {this.props.children}
          </ComponentsErrorBoundary>
        </div>

        <div id='g-search'>
          <ComponentsErrorBoundary name='G_Search'>
            <SearchBar />
          </ComponentsErrorBoundary>
        </div>

        <ComponentsErrorBoundary name='G_Footer'>
          <LazyLoad offset={200}>{!app.G_HideHeaderFooter && <Footer />}</LazyLoad>
        </ComponentsErrorBoundary>

        <Common />
      </div>
    );
  }
}

const Common = inject(
  'area',
  'order',
  'product',
  'meta',
  'app',
  'auth'
)(
  observer((props) => {
    const { app } = props;
    const { G_GlobalLoading, GlobalVideoPopPlayer, curGlobalActivatedModal } = app;
    const { areaModalVisible } = props.area;
    const { liveInfo } = props.meta;
    const { createOrderLoading, _beingPaid, TaxModalForMXVisible } = props.order;
    const { subscribeCommodityModalVisible } = props.product;

    const [initCookieBot, setInitCookieBot] = useState(false);
    // 是否展示购物引导弹窗
    const [isShowLiveShoppingModal, setIsShowLiveShoppingModal] = useState(false);

    // 更新购物引导弹窗状态
    const changeIsShowLiveShoppingModal = (isShow) => {
      setIsShowLiveShoppingModal(isShow);
    };

    useEffect(() => {
      const isCookieBotScanner = window?.__NEXT_DATA__?.props?.pageData?.runtimeInject?.isCookieBotScanner ?? false;
      if (!isCookieBotScanner) {
        setInitCookieBot(true);
        return;
      }
      try {
        const instance = new CookieConsent({
          LANG: props.area.LANG,
          MobxApp: props.app,
          MobxAuth: props.auth,
        });
        instance.loadSnippets('preferences', true);
        instance.loadSnippets('statistics', true);
        instance.loadSnippets('marketing', true);
      } catch (e) {
        console.error(e);
      }
    }, []);

    function EnabledComponent(key) {
      switch (key) {
        case 'CookieBot':
          if (!initCookieBot) return null;
          return <CookieBot />;
        case 'OrderSubmitLoading':
          if (!createOrderLoading && !_beingPaid) return null;
          return <OrderSubmitLoading />;
        case 'ArrivalNoticeModal':
          if (!subscribeCommodityModalVisible) return null;
          return <ArrivalNoticeModal />;
        case 'TaxModalForMX':
          if (!TaxModalForMXVisible) return null;
          return <TaxModalForMX />;
        case 'AreaSelectorModal':
          if (!areaModalVisible) return null;
          return <AreaSelectorModal />;
        case 'GlobalVideoPopPlayer':
          if (!GlobalVideoPopPlayer.src) return null;
          return <GlobalVideoPopPlayerModal />;
        case 'LiveFB':
          if (!liveInfo) return null;
          return <LiveFB />;
        case 'UserSubscribeModal':
          // 如果购物引导弹窗已展示，则不展示邮件订阅弹窗
          if (!curGlobalActivatedModal?.UserSubscribeModal || isShowLiveShoppingModal) return null;
          return <SubscribeModal />;
        case 'LiveShoppingModal':
          return <LiveShoppingModal handleChangeIsShowLiveShoppingModal={changeIsShowLiveShoppingModal} />;
        default:
          break;
      }
    }
    return (
      <ComponentsErrorBoundary name='G_Common'>
        {/* CookieBot */}
        {EnabledComponent('CookieBot')}

        {/* 邮件订阅弹窗 */}
        {EnabledComponent('UserSubscribeModal')}

        {/* FB直播 */}
        {EnabledComponent('LiveFB')}

        {/* 地区选择 */}
        {EnabledComponent('AreaSelectorModal')}

        {/* 移动端地区确认 */}
        {EnabledComponent('AreaSelectorConfirmModal')}

        {/* 墨西哥税费提示 */}
        {EnabledComponent('TaxModalForMX')}

        {/* 到货通知订阅弹窗 */}
        {EnabledComponent('ArrivalNoticeModal')}

        {/* 支付loading */}
        {EnabledComponent('OrderSubmitLoading')}

        {/* 全局视频弹窗播放 */}
        {EnabledComponent('GlobalVideoPopPlayer')}

        {/* 直播导购小窗 */}
        {EnabledComponent('LiveShoppingModal')}

        {G_GlobalLoading && <Loading className='global-spin-loading' />}

        {/* 页面右下角浮动工具栏 */}
        <PageToolBar />
      </ComponentsErrorBoundary>
    );
  })
);

const GlobalVideoPopPlayerModal = inject(
  'area',
  'app'
)(
  observer((props) => {
    const { GlobalVideoPopPlayer } = props.app;
    return (
      <PopupPlayer
        type='normal'
        poster={GlobalVideoPopPlayer?.poster}
        url={GlobalVideoPopPlayer?.src}
        onCancel={GlobalVideoPopPlayer?.closeCallback}
        visible={!!GlobalVideoPopPlayer?.src}
        trackPlayTimeParams={GlobalVideoPopPlayer?.trackPlayTimeParams}
      />
    );
  })
);

export default Layout;
